/* eslint-disable @typescript-eslint/no-non-null-assertion,no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { MapboxGeoJSONFeature } from 'mapbox-gl';
import './Map.css';
import { AppBar, Box, Button, Checkbox, FormControlLabel, Menu, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import _ from 'lodash';
import Slider from '@mui/material/Slider';
import VideoDetails from './VideoDetails';
import { VideoFeature } from '../model/VideoFeature';
import { getTagById } from '../data/TagDictionary';
import StatusFilter from './StatusFilter';
import TagsFilter from './TagsFilter';
import IataField from './IataField';
import dataIndicativeSearch from '../data/skyscanner.model.IndicativeSearch.json';
import IndicativeSearch from '../data/skyscanner.model.IndicativeSearch';

mapboxgl.accessToken = 'pk.eyJ1IjoiZW1pcnBhc2ljYmQiLCJhIjoiY21rd2o1OW9zMDJhaDNncXZvNTByZjhndCJ9.JydciSd6gOuEODi6c16V1g';

const vectorLayer = 'mapbox://emirpasicbd.a8707qzw';
const sourceLayer = 'videos';

interface ExperimentEntry {
  price: number;
  city: string;
  country: string;
  iata: string;
  lat: number;
  lng: number;
}

const processExperimentEntries = (): ExperimentEntry[] => {
  const indicativeSearch = dataIndicativeSearch as IndicativeSearch;
  const { results } = indicativeSearch.content;
  const { quotes, places } = results;
  const output: ExperimentEntry[] = [];
  for (const [, quote] of Object.entries(quotes)) {
    const price = +quote.minPrice.amount;

    let place = places[quote.outboundLeg.destinationPlaceId];

    const lat = place.coordinates.latitude;
    const lng = place.coordinates.longitude;
    const { iata } = place;

    let city = '';
    let country = '';
    do {
      if (place.type === 'PLACE_TYPE_CITY') {
        city = place.name;
      } else if (place.type === 'PLACE_TYPE_COUNTRY') {
        country = place.name;
        break;
      }
      place = places[place.parentId];
    } while (place);

    output.push({ price, city, country, iata, lat, lng });
  }
  return _.uniqBy(output, 'iata');
};
const allExperimentEntries = processExperimentEntries();

// noinspection TypeScriptValidateTypes
const Map = () => {
  const mapContainerRef = useRef(null);
  const searchParams = new URLSearchParams(document.location.search);
  const isAdmin = searchParams.get('admin') !== null;
  const isExperiment = searchParams.get('experiment') !== null;

  const [experimentEntries, setExperimentEntries] = useState<ExperimentEntry[]>(allExperimentEntries);

  const userCoordinate = { lat: 52.513782, lng: 13.3966104 };
  const fromIatas = ['Berlin, Germany (BER)'];
  const toIatas = experimentEntries.map(({
    city,
    country,
    iata,
  }) => `${[city, country].filter(Boolean).join(', ')} (${iata})`).sort();

  const [lat, setLat] = useState(40);
  const [lng, setLng] = useState(10);
  const [zoom, setZoom] = useState(2);
  const [openVideoDetails, setOpenVideoDetails] = useState(false);
  const [selectedVideoFeatures, setSelectedVideoFeatures] = useState(Array<VideoFeature>);
  const [map, setMap] = React.useState<mapboxgl.Map>();
  const [statuses, setStatuses] = React.useState<string[]>(['ACCEPT', 'ACCEPT_IN_PROCESS', 'MAYBE']);
  const [tagIds, setTagIds] = React.useState<string[] | null>();
  const [satelliteMap, setSatelliteMap] = useState(false);
  const [heatMap, setHeatMap] = React.useState<boolean>(false);
  const [circleMap, setCircleMap] = React.useState<boolean>(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [fromIata, setFromIata] = React.useState<null | string>(fromIatas[0]);
  const [toIata, setToIata] = React.useState<null | string>(null);
  const [priceRange, setPriceRange] = React.useState<number[]>([0, Math.max(...experimentEntries.map(({ price }) => price))]);

  const updateMapFilter = (m: mapboxgl.Map | undefined) => {
    if (!m) return;
    const tagsFilter = tagIds?.length ? tagIds.map(tagId => ['in', `|${tagId}|`, ['get', 'tags']]) : [];
    const statusFilters = statuses?.length ? statuses.map(status => ['==', status, ['get', 'status']]) : [];
    for (const layer of ['videos-heat', 'videos']) {
      if (tagsFilter.length && statusFilters.length) {
        const filter = ['all', ['any', ...tagsFilter], ['any', ...statusFilters]];
        m.setFilter(layer, filter);
      } else if (tagsFilter.length && !statusFilters.length) {
        m.setFilter(layer, ['any', ...tagsFilter]);
      } else if (!tagsFilter.length && statusFilters.length) {
        m.setFilter(layer, ['any', ...statusFilters]);
      } else {
        m.setFilter(layer, null);
      }
    }
    m.setLayoutProperty('videos-heat', 'visibility', heatMap ? 'visible' : 'none');
    m.setLayoutProperty('videos', 'visibility', circleMap ? 'visible' : 'none');
    m.setLayoutProperty('videos-highlighted', 'visibility', circleMap ? 'visible' : 'none');
  };

  useEffect(() => {
    if (map?.getLayer('route')) {
      map?.removeLayer('route-label');
      map?.removeLayer('route');
      map?.removeSource('route');
    }
    if (toIata) {
      const onlyIata = toIata.length === 3 ? toIata : toIata.match(/\(([A-Z]{3})\)/)?.[1];
      map?.setFilter('airports-highlighted', ['==', 'iata', onlyIata || '']);

      const entry = allExperimentEntries.find(({ iata }) => iata === onlyIata);
      if (!entry) return;
      map?.addLayer({
        id: 'route',
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [userCoordinate.lng, userCoordinate.lat],
                [entry.lng, entry.lat],
              ],
            },
          },
        },
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': '#6143BC',
          'line-width': 3,
          'line-opacity': 0.75,
          'line-dasharray': [2, 2],
        },
      });
      map?.addLayer({
        id: 'route-label',
        type: 'symbol',
        source: 'route',
        layout: {
          'symbol-placement': 'line-center',
          'text-field': `€${entry.price}`,
          'text-size': 16,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#6143BC',
          'text-halo-width': 1,
        },
      });
    } else {
      map?.setFilter('airports-highlighted', ['==', 'iata', '']);
    }
  }, [toIata]);

  // Initialize map when component mounts
  useEffect(() => {
    if (!mapContainerRef) return;
    const m = new mapboxgl.Map({
      container: mapContainerRef.current!,
      style: satelliteMap ? 'mapbox://styles/emirpasicbd/clhqtom8f01sr01r0h38oam3h' : 'mapbox://styles/emirpasicbd/clhkmarye01mk01pgg6orc7np',
      center: [lng, lat],
      zoom,
      projection: { name: 'globe' },
    });
    setMap(m);

    m.on('move', () => {
      setLng(m.getCenter().lng);
      setLat(m.getCenter().lat);
      setZoom(m.getZoom());
    });

    m.on('load', () => {
      m.addSource('videos', {
        type: 'vector',
        url: vectorLayer,
      });

      m.addLayer(
        {
          id: 'videos',
          type: 'circle',
          source: 'videos',
          'source-layer': sourceLayer,
          layout: {
            visibility: circleMap ? 'visible' : 'none',
          },
          paint: {
            'circle-color': isAdmin ? [
              'match',
              ['get', 'status'],
              'ACCEPT',
              'rgba(0,255,0,0.5)',
              'DENY',
              'rgba(255,0,0,0.5)',
              // other (TO_BE_REVIEWED,MAYBE,ACCEPT_IN_PROCESS)
              'rgba(255,255,0,0.5)',
            ] : isExperiment ? [
              'match',
              ['get', 'status'],
              'ACCEPT',
              'rgba(0,200,0,0.5)',
              'DENY',
              'rgba(200,0,0,0.5)',
              // other (TO_BE_REVIEWED,MAYBE,ACCEPT_IN_PROCESS)
              'rgba(200,200,0,0.5)',
            ]
              : 'rgba(255,255,255,0.8)',
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 2,
              2, 3,
              4, 4,
              7, 5,
              15, 6,
            ],
            'circle-blur': 1,
          },
        },
      );

      m.addLayer(
        {
          id: 'videos-highlighted',
          type: 'circle',
          source: 'videos',
          'source-layer': sourceLayer,
          layout: {
            visibility: circleMap ? 'visible' : 'none',
          },
          paint: {
            'circle-color': isAdmin ? [
              'match',
              ['get', 'status'],
              'ACCEPT',
              'rgba(0,255,0,0.5)',
              'DENY',
              'rgba(255,0,0,0.5)',
              // other (TO_BE_REVIEWED,MAYBE,ACCEPT_IN_PROCESS)
              'rgba(255,255,0,0.5)',
            ] : 'rgba(255,255,255,0.8)',
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 2,
              2, 3,
              4, 4,
              7, 5,
              15, 6,
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': 'rgba(255,255,255,0.9)',
          },
          filter: ['any', ['in', 'XXX', ['get', 'shortCode']]],
        },
      );

      m.addLayer(
        {
          id: 'videos-heat',
          type: 'heatmap',
          source: 'videos',
          'source-layer': sourceLayer,
          maxzoom: 15,
          layout: {
            visibility: heatMap ? 'visible' : 'none',
          },
          paint: {
            // Increase the heatmap color weight weight by zoom level
            // heatmap-intensity is a multiplier on top of heatmap-weight
            'heatmap-intensity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 1,
              10, 2,
            ],
            // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
            // Begin color ramp at 0-stop with a 0-transparancy color
            // to create a blur-like effect.
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0, 'rgba(255,255,255,0)',
              0.2, 'rgba(255,255,255,0.2)',
              0.4, 'rgba(255,255,255,0.4)',
              0.9, 'rgba(255,255,255,0.6)',
              1.0, 'rgba(255,255,255,0.8)',
            ],
            // Adjust the heatmap radius by zoom level
            'heatmap-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 4,
              3, 10,
              10, 30,
            ],
            // // Transition from heatmap to circle layer by zoom level
            'heatmap-opacity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              7, 1,
              9, 0,
            ],
          },
        },
      );

      if (isExperiment) {
        const marker = new mapboxgl.Marker({ color: '#6143BC', scale: 1 });
        marker.setLngLat(userCoordinate).addTo(m);
        m.addSource('airports', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: experimentEntries.map(({ lat: y, lng: x, city, country, iata, price }) => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [x, y],
              },
              properties: {
                title: `€${price}\n${[city, country].filter(Boolean).join(', ')} (${iata})`,
                price,
                iata,
              },
            })),
          },
        });

        m.addLayer({
          id: 'airports',
          type: 'symbol',
          source: 'airports',
          layout: {
            'icon-image': 'airport',
            'icon-size': 0.8,
            'text-field': ['get', 'title'],
            'text-offset': [0, 0.75],
            'text-anchor': 'top',
            'text-size': 13,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0, 0, 0, 0.5)',
            'text-halo-width': 2,
            'text-halo-blur': 1,

          },
        });

        m.addLayer({
          id: 'airports-highlighted',
          type: 'symbol',
          source: 'airports',
          layout: {
            'icon-image': 'airport',
            'icon-size': 1.2,
            'text-field': ['get', 'title'],
            'text-offset': [0, 0.75],
            'text-anchor': 'top',
            'text-size': 13,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#6143BC',
            'text-halo-width': 1,
          },
          filter: ['==', 'iata', ''],
        });

        m.on('mouseenter', 'airports', () => m.getCanvas().style.cursor = 'pointer');
        m.on('mouseleave', 'airports', () => m.getCanvas().style.cursor = 'grab');
      }

      m.on('click', e => {
        if (isExperiment) {
          const selectedFeatures: MapboxGeoJSONFeature[] = m.queryRenderedFeatures([
            [e.point.x, e.point.y],
            [e.point.x, e.point.y],
          ], { layers: ['airports'] });

          const iatas = selectedFeatures.map(feature => feature.properties!.iata);

          setToIata(iatas[0] || null);
          if (iatas.length) return;
        }

        let selectedFeatures: MapboxGeoJSONFeature[] = [];
        for (const radius of [1, 5, 10, 15, 20]) {
          const bbox: [[number, number], [number, number]] = [
            [e.point.x - radius, e.point.y - radius],
            [e.point.x + radius, e.point.y + radius],
          ];
          selectedFeatures = m.queryRenderedFeatures(bbox, { layers: ['videos'] });
          if (selectedFeatures.length) break;
        }

        const shortCodes = selectedFeatures.map(
          feature => feature.properties!.shortCode,
        );
        if (shortCodes.length) {
          m.setFilter('videos-highlighted', ['any', ...shortCodes.map(shortCode => (['in', shortCode, ['get', 'shortCode']]))]);
          setSelectedVideoFeatures(selectedFeatures.map(feature => {
            const { tags } = feature.properties!;
            return {
              ...feature.properties!,
              tagsIds: tags,
              tags: (tags as string).split('|').filter(Boolean).map(tagId => getTagById(Number(tagId))),
            } as VideoFeature;
          }));
          setOpenVideoDetails(true);
        } else {
          m?.setFilter('videos-highlighted', ['any', ['in', 'XXX', ['get', 'shortCode']]]);
        }
      });

      m.on('mouseenter', 'videos', () => m.getCanvas().style.cursor = 'pointer');
      m.on('mouseleave', 'videos', () => m.getCanvas().style.cursor = 'grab');

      setTimeout(() => updateMapFilter(m));

      m.on('mousedown', () => m.getCanvas().style.cursor = 'grabbing');
      m.on('mouseup', () => m.getCanvas().style.cursor = 'grab');
    });

    // Clean up on unmount
    return () => m.remove();
  }, [mapContainerRef, satelliteMap]);

  const onVideoDetailsClose = () => {
    setSelectedVideoFeatures([]);
    setOpenVideoDetails(false);
    map?.setFilter('videos-highlighted', ['any', ['in', 'XXX', ['get', 'shortCode']]]);
  };

  useEffect(() => updateMapFilter(map), [statuses, tagIds, heatMap, circleMap]);

  useEffect(() => {
    if (!map) return;
    map.setFilter('airports', ['all', ['>=', 'price', priceRange[0]], ['<=', 'price', priceRange[1]]]);
    setExperimentEntries(allExperimentEntries.filter(entry => entry.price >= priceRange[0] && entry.price <= priceRange[1]));
  }, [priceRange]);

  return (
    <div>
      <div className='map-container' ref={mapContainerRef}/>
      <AppBar className="app-bar">
        <VideoDetails open={openVideoDetails}
          onClose={onVideoDetailsClose}
          videoFeatures={selectedVideoFeatures}
          isAdmin={isAdmin}
        />
        {isAdmin
          && <StatusFilter statusFilterChange={_statuses => setStatuses(_statuses)} statuses={statuses}/>
        }
        {!isExperiment
          && <TagsFilter tagIdsChanged={tagsId => setTagIds(tagsId)} isAdmin={isAdmin}/>
        }
        {isExperiment
          && <IataField
            iataChanged={iata => setFromIata(iata)}
            iatas={fromIatas}
            selectedIata={fromIata}
            label="From"
            width={300}/>}
        {isExperiment
          && <IataField
            iataChanged={iata => setToIata(iata)}
            iatas={toIatas}
            selectedIata={toIata}
            label="To"
            width={400}/>}
        {isExperiment
          && <Box sx={{ width: 300 }} className="price-range">
            <Typography gutterBottom>Price from €{priceRange[0]} to €{priceRange[1]}</Typography>
            <Slider
              value={priceRange}
              onChange={(e, v) => setPriceRange(v as number[])}
              valueLabelDisplay="auto"
              valueLabelFormat={(value: number) => `€${value}`}
              disableSwap
              min={0}
              max={Math.max(...allExperimentEntries.map(({ price }) => price))}
            />
          </Box>}
        {!isExperiment
          && <>
            <Button
              id="demo-positioned-button"
              aria-controls={anchorEl ? 'demo-positioned-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
              onClick={e => setAnchorEl(e.currentTarget)}>
              Map Type
            </Button>
            <Menu
              id="demo-positioned-menu"
              aria-labelledby="demo-positioned-button"
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}>
              <MenuItem onClick={() => setAnchorEl(null)}>
                <FormControlLabel
                  control={
                    <Checkbox checked={satelliteMap} onChange={e => setSatelliteMap(e.target.checked)}/>
                  }
                  label="Satellite"
                  className="checkbox"
                />
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>
                <FormControlLabel
                  control={
                    <Checkbox checked={circleMap} onChange={e => setCircleMap(e.target.checked)}/>
                  }
                  label="Dot"
                  className="checkbox"
                />
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>
                <FormControlLabel
                  control={
                    <Checkbox checked={heatMap} onChange={e => setHeatMap(e.target.checked)}/>
                  }
                  label="Heat"
                  className="checkbox"
                />
              </MenuItem>
            </Menu>
          </>
        }

      </AppBar>
    </div>
  );
};

export default Map;
